import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ImageMask } from 'elements/visual/ImageMask';
import { motion } from 'framer-motion';
import {
    ANIMATION_BIGGER,
    ANIMATION_SMALLER,
    conditionalScaleAnimation
} from 'libs/styles/animations';
import { EBorders, EColors, EFonts, EGrads, EHovers } from 'libs/styles/styling';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
    icon?: {
        iconName: IconName;
        iconSet?: IconPrefix;
    };
    title: string;
    expanded?: boolean;
    onExpandClick?: () => void;
    highlighted?: boolean;
    children?: React.ReactNode;
    topBorder?: boolean;
}

export const SidebarSectionHeader = (props: Props): JSX.Element => {
    return (
        <S_SidebarSectionHeader
            $expandable={props.onExpandClick ? true : false}
            $highlighted={props.highlighted}
            className={props.className}
            onClick={() => {
                props.onExpandClick?.();
            }}
            $topBorder={props.topBorder}
        >
            {props.icon && (
                <S_HeaderIcon
                    icon={[props.icon.iconSet ?? 'fal', props.icon.iconName]}
                    $highlighted={props.highlighted}
                />
            )}
            <S_HeaderTitle $highlighted={props.highlighted}>{props.title}</S_HeaderTitle>
            {props.onExpandClick && (
                <motion.div
                    {...conditionalScaleAnimation(true, ANIMATION_BIGGER, ANIMATION_SMALLER)}
                    style={{ height: '29px' }}
                >
                    <S_ExpandIcon
                        icon={['fal', props.expanded ? 'minus' : 'arrows-up-down']}
                        $highlighted={props.highlighted}
                    />
                </motion.div>
            )}
            {props.highlighted && (
                <ImageMask randomizePosition={true} step={20} image="contour" repeat={true} />
            )}
            {props.children && <S_Children>{props.children}</S_Children>}
        </S_SidebarSectionHeader>
    );
};

const S_SidebarSectionHeader = styled.div<{
    $expandable: boolean;
    $highlighted: boolean;
    $topBorder?: boolean;
}>`
    display: flex;
    width: 100%;
    color: #141c54;
    background: ${EColors.GREY5};
    flex-direction: row;
    padding: 9px 15px 10px 20px;
    align-items: center;
    border-bottom: 1px solid ${EBorders.DARK};
    margin-bottom: 0;
    cursor: ${(props) => (props.$expandable ? 'pointer' : 'default')};
    position: relative;
    border-top: ${(props) => (props.$topBorder ? '1px solid' + EBorders.DARK : 'none')};
    ${(props) => {
        if (props.$highlighted) {
            return css`
                background: ${EGrads.ACC2_GRAD};
            `;
        }
    }}
    user-select: none;
    ${(props) => {
        if (!props.$highlighted && props.$expandable) {
            return css`
                &:hover {
                    box-shadow: ${EHovers.DARK1};
                }
            `;
        }
    }}
`;

const S_HeaderIcon = styled(FontAwesomeIcon)<{ $highlighted: boolean }>`
    font-size: 20px;
    margin: 0 15px 0 0;
    width: 20px;
    color: ${(props) => (props.$highlighted ? EColors.WHITE : EColors.DARK)};
`;

const S_HeaderTitle = styled.div<{ $highlighted: boolean }>`
    font-family: ${EFonts.Regular};
    font-size: 14px;
    margin-right: auto;
    margin-left: 0;
    padding: 6px 0;
    color: ${(props) => (props.$highlighted ? EColors.WHITE : EColors.DARK)};
`;

const S_ExpandIcon = styled(FontAwesomeIcon)<{ $highlighted: boolean }>`
    padding: 5px;
    font-size: 20px;
    justify-self: end;
    margin-left: auto;
    color: ${(props) => (props.$highlighted ? EColors.WHITE : EColors.DARK)};
`;

const S_Children = styled.div`
    padding: 0 5px 0 0;
    justify-self: end;
    margin-left: auto;
`;
